import * as turf from '@turf/turf';
import { lv95ToWgs84ForOneCoordinate, wgs84ToLv95ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';

type Coordinate = {
  x: number;
  y: number;
};

type DeriveBuildableBorderResult = {
  buildableBorder: Coordinate[];
  error: string | null;
};

type BuildableOffsetPreviewSegment = {
  start: Coordinate;
  end: Coordinate;
};

const MIN_VERTEX_COUNT = 3;

const INVALID_BUILDABLE_AREA_ERROR = 'The selected offsets do not produce a valid buildable area.';
const COLLAPSED_CORNER_ERROR = (previousSideIndex: number, currentSideIndex: number) =>
  `The selected offsets collapse the corner between sides ${previousSideIndex} and ${currentSideIndex}.`;
const SELF_INTERSECTION_ERROR = 'The selected offsets make the buildable area intersect itself.';
const UNIFORM_OFFSET_COLLAPSE_ERROR =
  'The selected uniform offset is too large for this property area and collapses the buildable area.';

const MIN_LINE_LENGTH = 1e-9;
const LINE_INTERSECTION_EPSILON = 1e-7;
const POINT_MERGE_EPSILON = 0.01;
const OFFSET_EQUALITY_EPSILON = 1e-6;
type GeoJsonPolygonFeature = GeoJSON.Feature<GeoJSON.Polygon>;
type GeoJsonPolygonOrMultiPolygon =
  | GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon;

const getBuildableOffsetSourceBorder = (
  legalBorder: Coordinate[] = [],
  buildableBorder: Coordinate[] = []
): Coordinate[] => {
  if (Array.isArray(buildableBorder) && buildableBorder.length >= MIN_VERTEX_COUNT) {
    return buildableBorder;
  }

  if (Array.isArray(legalBorder) && legalBorder.length >= MIN_VERTEX_COUNT) {
    return legalBorder;
  }

  return [];
};

const createBuildableSegmentOffsets = (
  sideCount: number,
  selectedIndex: number,
  selectedOffset: number,
  remainingOffset: number | null = null
): number[] => {
  if (!Number.isInteger(sideCount) || sideCount < MIN_VERTEX_COUNT) {
    return [];
  }

  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= sideCount) {
    return [];
  }

  const sharedOffset = remainingOffset == null ? 0 : remainingOffset;
  const offsets = Array(sideCount).fill(sharedOffset);
  offsets[selectedIndex] = selectedOffset;
  return offsets;
};

const buildTurfPolygonFromLv95 = (coordinates: Coordinate[] = []): GeoJsonPolygonFeature | null => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) {
    return null;
  }

  const ring = coordinates.map((coordinate) => {
    const { lng, lat } = lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y);
    return [lng, lat] as [number, number];
  });

  if (ring.some(([lng, lat]) => !Number.isFinite(lng) || !Number.isFinite(lat))) {
    return null;
  }

  ring.push(ring[0]);
  return turf.polygon([ring]);
};

const getPolygonArea = (coordinates: Coordinate[] = []): number => Math.abs(getSignedArea(coordinates));

const normalizeRingCoordinates = (coordinates: Coordinate[] = []): Coordinate[] => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) {
    return [];
  }

  const normalized: Coordinate[] = [];

  for (const coordinate of coordinates) {
    if (!Number.isFinite(coordinate.x) || !Number.isFinite(coordinate.y)) {
      continue;
    }

    const previousAcceptedCoordinate = normalized[normalized.length - 1];
    if (previousAcceptedCoordinate && arePointsNear(coordinate, previousAcceptedCoordinate, POINT_MERGE_EPSILON / 10)) {
      continue;
    }

    normalized.push(coordinate);
  }

  if (
    normalized.length >= 2 &&
    arePointsNear(normalized[0], normalized[normalized.length - 1], POINT_MERGE_EPSILON / 10)
  ) {
    normalized.pop();
  }

  return normalized;
};

const extractLargestPolygonRing = (geometry: GeoJsonPolygonOrMultiPolygon | null | undefined): Coordinate[] => {
  if (!geometry) {
    return [];
  }

  const polygons =
    geometry.type === 'Feature'
      ? geometry.geometry.type === 'Polygon'
        ? [geometry.geometry.coordinates?.[0] ?? []]
        : geometry.geometry.coordinates.map((polygon: GeoJSON.Position[][]) => polygon?.[0] ?? [])
      : geometry.type === 'Polygon'
        ? [geometry.coordinates?.[0] ?? []]
        : geometry.coordinates.map((polygon: GeoJSON.Position[][]) => polygon?.[0] ?? []);

  const candidateRings = polygons
    .map((ring: GeoJSON.Position[]) =>
      normalizeRingCoordinates(
        ring
          .map((coordinate: GeoJSON.Position) =>
            wgs84ToLv95ForOneCoordinate(Number(coordinate?.[0]), Number(coordinate?.[1]))
          )
          .filter((coordinate: Coordinate) => Number.isFinite(coordinate.x) && Number.isFinite(coordinate.y))
      )
    )
    .filter((ring: Coordinate[]) => ring.length >= MIN_VERTEX_COUNT);

  if (!candidateRings.length) {
    return [];
  }

  return candidateRings.reduce((largestRing: Coordinate[], currentRing: Coordinate[]) =>
    getPolygonArea(currentRing) > getPolygonArea(largestRing) ? currentRing : largestRing
  );
};

const deriveUniformBuildableBorderFromBuffer = (
  legalBorder: Coordinate[] = [],
  uniformOffset: number
): DeriveBuildableBorderResult | null => {
  if (!Number.isFinite(uniformOffset) || uniformOffset < 0) {
    return null;
  }

  if (uniformOffset === 0) {
    return {
      buildableBorder: normalizeRingCoordinates(legalBorder),
      error: null,
    };
  }

  const legalPolygon = buildTurfPolygonFromLv95(legalBorder);
  if (!legalPolygon) {
    return null;
  }

  try {
    const buffered = turf.buffer(legalPolygon, -uniformOffset, {
      units: 'meters',
      steps: 8,
    });

    if (!buffered) {
      return {
        buildableBorder: [],
        error: UNIFORM_OFFSET_COLLAPSE_ERROR,
      };
    }

    const buildableBorder = extractLargestPolygonRing(buffered);
    if (buildableBorder.length < MIN_VERTEX_COUNT) {
      return {
        buildableBorder: [],
        error: UNIFORM_OFFSET_COLLAPSE_ERROR,
      };
    }

    return {
      buildableBorder,
      error: null,
    };
  } catch (error) {
    return {
      buildableBorder: [],
      error: UNIFORM_OFFSET_COLLAPSE_ERROR,
    };
  }
};

const getSignedArea = (coordinates: Coordinate[] = []): number => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) {
    return 0;
  }

  let area = 0;
  for (let index = 0; index < coordinates.length; index += 1) {
    const current = coordinates[index];
    const next = coordinates[(index + 1) % coordinates.length];
    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
};

const arePointsNear = (left: Coordinate, right: Coordinate, epsilon = POINT_MERGE_EPSILON): boolean =>
  Math.hypot(left.x - right.x, left.y - right.y) <= epsilon;

const allOffsetsAreUniform = (segmentOffsets: number[] = []): boolean => {
  if (!segmentOffsets.length) {
    return false;
  }

  const baseline = Number(segmentOffsets[0]);
  if (!Number.isFinite(baseline)) {
    return false;
  }

  return segmentOffsets.every(
    (offset) => Number.isFinite(Number(offset)) && Math.abs(Number(offset) - baseline) <= OFFSET_EQUALITY_EPSILON
  );
};

const offsetPoint = (point: Coordinate, normal: Coordinate, distance: number): Coordinate => ({
  x: point.x + normal.x * distance,
  y: point.y + normal.y * distance,
});

const dotProduct = (left: Coordinate, right: Coordinate): number => left.x * right.x + left.y * right.y;

const subtractVectors = (left: Coordinate, right: Coordinate): Coordinate => ({
  x: left.x - right.x,
  y: left.y - right.y,
});

const getSignedDistanceToOffsetLine = (point: Coordinate, linePoint: Coordinate, inwardNormal: Coordinate): number =>
  dotProduct(subtractVectors(point, linePoint), inwardNormal);

const intersectSegmentWithOffsetLine = (
  segmentStart: Coordinate,
  segmentEnd: Coordinate,
  linePoint: Coordinate,
  inwardNormal: Coordinate
): Coordinate | null => {
  const segmentVector = subtractVectors(segmentEnd, segmentStart);
  const denominator = dotProduct(segmentVector, inwardNormal);

  if (Math.abs(denominator) <= LINE_INTERSECTION_EPSILON) {
    return null;
  }

  const t = -getSignedDistanceToOffsetLine(segmentStart, linePoint, inwardNormal) / denominator;
  if (!Number.isFinite(t)) {
    return null;
  }

  return {
    x: segmentStart.x + segmentVector.x * t,
    y: segmentStart.y + segmentVector.y * t,
  };
};

const clipPolygonWithHalfPlane = (
  polygon: Coordinate[] = [],
  linePoint: Coordinate,
  inwardNormal: Coordinate
): Coordinate[] => {
  if (!Array.isArray(polygon) || polygon.length < MIN_VERTEX_COUNT) {
    return [];
  }

  const clipped: Coordinate[] = [];

  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    const currentDistance = getSignedDistanceToOffsetLine(current, linePoint, inwardNormal);
    const nextDistance = getSignedDistanceToOffsetLine(next, linePoint, inwardNormal);
    const currentInside = currentDistance >= -LINE_INTERSECTION_EPSILON;
    const nextInside = nextDistance >= -LINE_INTERSECTION_EPSILON;

    if (currentInside && nextInside) {
      clipped.push(next);
      continue;
    }

    const intersection = intersectSegmentWithOffsetLine(current, next, linePoint, inwardNormal);

    if (currentInside && !nextInside) {
      if (intersection) {
        clipped.push(intersection);
      }
      continue;
    }

    if (!currentInside && nextInside) {
      if (intersection) {
        clipped.push(intersection);
      }
      clipped.push(next);
    }
  }

  return normalizeRingCoordinates(clipped);
};

const getInwardNormal = (start: Coordinate, end: Coordinate, isCounterClockwise: boolean): Coordinate | null => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);

  if (length <= MIN_LINE_LENGTH) {
    return null;
  }

  if (isCounterClockwise) {
    return {
      x: -dy / length,
      y: dx / length,
    };
  }

  return {
    x: dy / length,
    y: -dx / length,
  };
};

const MITER_LIMIT_MULTIPLIER = 8;
const MIN_MITER_LIMIT_METERS = 5;

const intersectInfiniteLines = (
  lineAStart: Coordinate,
  lineAEnd: Coordinate,
  lineBStart: Coordinate,
  lineBEnd: Coordinate
): Coordinate | null => {
  const denominator =
    (lineAStart.x - lineAEnd.x) * (lineBStart.y - lineBEnd.y) -
    (lineAStart.y - lineAEnd.y) * (lineBStart.x - lineBEnd.x);

  if (Math.abs(denominator) <= LINE_INTERSECTION_EPSILON) {
    return null;
  }

  const determinantA = lineAStart.x * lineAEnd.y - lineAStart.y * lineAEnd.x;
  const determinantB = lineBStart.x * lineBEnd.y - lineBStart.y * lineBEnd.x;

  return {
    x: (determinantA * (lineBStart.x - lineBEnd.x) - (lineAStart.x - lineAEnd.x) * determinantB) / denominator,
    y: (determinantA * (lineBStart.y - lineBEnd.y) - (lineAStart.y - lineAEnd.y) * determinantB) / denominator,
  };
};

const deriveSingleSideBuildableBorder = (
  legalBorder: Coordinate[] = [],
  sideIndex: number,
  offsetDistance: number
): DeriveBuildableBorderResult | null => {
  if (!Array.isArray(legalBorder) || legalBorder.length < MIN_VERTEX_COUNT) {
    return null;
  }

  if (!Number.isInteger(sideIndex) || sideIndex < 0 || sideIndex >= legalBorder.length) {
    return null;
  }

  if (!Number.isFinite(offsetDistance) || offsetDistance < 0) {
    return null;
  }

  if (offsetDistance <= OFFSET_EQUALITY_EPSILON) {
    return {
      buildableBorder: normalizeRingCoordinates(legalBorder),
      error: null,
    };
  }

  const signedArea = getSignedArea(legalBorder);
  if (Math.abs(signedArea) <= MIN_LINE_LENGTH) {
    return null;
  }

  const isCounterClockwise = signedArea > 0;
  const sideCount = legalBorder.length;
  const sideStartIndex = sideIndex;
  const sideEndIndex = (sideIndex + 1) % sideCount;
  const previousVertex = legalBorder[(sideStartIndex - 1 + sideCount) % sideCount];
  const nextVertex = legalBorder[(sideEndIndex + 1) % sideCount];
  const sideStart = legalBorder[sideStartIndex];
  const sideEnd = legalBorder[sideEndIndex];
  const inwardNormal = getInwardNormal(sideStart, sideEnd, isCounterClockwise);

  if (!inwardNormal) {
    return null;
  }

  const shiftedStart = offsetPoint(sideStart, inwardNormal, offsetDistance);
  const shiftedEnd = offsetPoint(sideEnd, inwardNormal, offsetDistance);
  const miterLimit = Math.max(offsetDistance * MITER_LIMIT_MULTIPLIER, MIN_MITER_LIMIT_METERS);

  // Extending to meet the neighboring edge's line (a "miter" join) is correct for ordinary
  // corners, but on cadastral borders the neighbor is often nearly parallel to the offset side
  // (many near-collinear points along a digitized curve), which sends that intersection far away
  // from the corner. When that happens, fall back to the raw perpendicular offset point (a
  // "bevel" join) instead of extending — this only ever touches the two vertices next to the
  // offset side and never reaches further into the polygon.
  const startMiter = intersectInfiniteLines(previousVertex, sideStart, shiftedStart, shiftedEnd);
  const newStart =
    startMiter && Math.hypot(startMiter.x - sideStart.x, startMiter.y - sideStart.y) <= miterLimit
      ? startMiter
      : shiftedStart;

  const endMiter = intersectInfiniteLines(shiftedStart, shiftedEnd, sideEnd, nextVertex);
  const newEnd =
    endMiter && Math.hypot(endMiter.x - sideEnd.x, endMiter.y - sideEnd.y) <= miterLimit ? endMiter : shiftedEnd;

  const buildableBorder = normalizeRingCoordinates(
    legalBorder.flatMap((coordinate, index) => {
      if (index === sideStartIndex) {
        return [newStart, newEnd];
      }

      if (index === sideEndIndex) {
        return [];
      }

      return [coordinate];
    })
  );

  if (buildableBorder.length < MIN_VERTEX_COUNT) {
    return {
      buildableBorder: [],
      error: INVALID_BUILDABLE_AREA_ERROR,
    };
  }

  const buildablePolygon = buildTurfPolygonFromLv95(buildableBorder);
  if (buildablePolygon && turf.kinks(buildablePolygon).features.length !== 0) {
    return {
      buildableBorder: [],
      error: SELF_INTERSECTION_ERROR,
    };
  }

  return {
    buildableBorder,
    error: null,
  };
};

const deriveBuildableBorderFromLegalBorder = (
  legalBorder: Coordinate[] = [],
  segmentOffsets: number[] = []
): DeriveBuildableBorderResult => {
  if (!Array.isArray(legalBorder) || legalBorder.length < 3) {
    return {
      buildableBorder: [],
      error: 'The property area must contain at least 3 vertices.',
    };
  }

  if (!Array.isArray(segmentOffsets) || segmentOffsets.length !== legalBorder.length) {
    return {
      buildableBorder: [],
      error: 'An offset must be defined for every property area side.',
    };
  }

  const nonZeroOffsetIndices = segmentOffsets.reduce<number[]>((indices, offset, index) => {
    if (Number.isFinite(Number(offset)) && Number(offset) > OFFSET_EQUALITY_EPSILON) {
      indices.push(index);
    }
    return indices;
  }, []);

  if (nonZeroOffsetIndices.length === 1) {
    const singleSideResult = deriveSingleSideBuildableBorder(
      legalBorder,
      nonZeroOffsetIndices[0],
      Number(segmentOffsets[nonZeroOffsetIndices[0]])
    );

    if (singleSideResult) {
      return singleSideResult;
    }
  }

  const normalizedOffset = Number(segmentOffsets[0]);
  const hasUniformOffsets =
    allOffsetsAreUniform(segmentOffsets) && Number.isFinite(normalizedOffset) && normalizedOffset >= 0;

  if (hasUniformOffsets) {
    const bufferedResult = deriveUniformBuildableBorderFromBuffer(legalBorder, normalizedOffset);
    if (bufferedResult) {
      return bufferedResult;
    }
  }

  const minimumOffset = Math.min(...segmentOffsets.map((offset) => Number(offset)));
  if (!Number.isFinite(minimumOffset) || minimumOffset < 0) {
    return {
      buildableBorder: [],
      error: 'Every side must have an offset greater than or equal to 0 meters.',
    };
  }

  const normalizedLegalBorder =
    minimumOffset > 0
      ? deriveUniformBuildableBorderFromBuffer(legalBorder, minimumOffset)?.buildableBorder || []
      : legalBorder;

  if (minimumOffset > 0 && normalizedLegalBorder.length < MIN_VERTEX_COUNT) {
    return {
      buildableBorder: [],
      error: UNIFORM_OFFSET_COLLAPSE_ERROR,
    };
  }

  const signedArea = getSignedArea(legalBorder);
  if (Math.abs(signedArea) <= MIN_LINE_LENGTH) {
    return {
      buildableBorder: [],
      error: 'The property area is degenerate.',
    };
  }

  const isCounterClockwise = signedArea > 0;
  let clippedBorder: Coordinate[] = [...normalizedLegalBorder];

  for (let index = 0; index < legalBorder.length; index += 1) {
    const start = legalBorder[index];
    const end = legalBorder[(index + 1) % legalBorder.length];
    const distance = Number(segmentOffsets[index]);

    if (!Number.isFinite(distance) || distance < 0) {
      return {
        buildableBorder: [],
        error: 'Every side must have an offset greater than or equal to 0 meters.',
      };
    }

    const inwardNormal = getInwardNormal(start, end, isCounterClockwise);
    if (!inwardNormal) {
      return {
        buildableBorder: [],
        error: 'The property area contains an invalid side.',
      };
    }

    const additionalOffset = distance - minimumOffset;
    if (additionalOffset <= OFFSET_EQUALITY_EPSILON) {
      continue;
    }

    const shiftedStart = offsetPoint(start, inwardNormal, distance);
    const nextBuildableBorder = clipPolygonWithHalfPlane(clippedBorder, shiftedStart, inwardNormal);
    if (nextBuildableBorder.length < MIN_VERTEX_COUNT || getPolygonArea(nextBuildableBorder) <= POINT_MERGE_EPSILON) {
      return {
        buildableBorder: [],
        error: COLLAPSED_CORNER_ERROR(index + 1, ((index + 1) % legalBorder.length) + 1),
      };
    }

    clippedBorder = nextBuildableBorder;
  }

  const buildableBorder = normalizeRingCoordinates(clippedBorder);
  if (buildableBorder.length < MIN_VERTEX_COUNT) {
    return {
      buildableBorder: [],
      error: INVALID_BUILDABLE_AREA_ERROR,
    };
  }

  const buildablePolygon = buildTurfPolygonFromLv95(buildableBorder);
  if (!buildablePolygon) {
    return {
      buildableBorder: [],
      error: INVALID_BUILDABLE_AREA_ERROR,
    };
  }

  const selfIntersect = turf.kinks(buildablePolygon);
  if (selfIntersect.features.length !== 0) {
    return {
      buildableBorder: [],
      error: SELF_INTERSECTION_ERROR,
    };
  }

  return {
    buildableBorder,
    error: null,
  };
};

const deriveBuildableOffsetPreviewSegments = (
  legalBorder: Coordinate[] = [],
  segmentOffsets: Array<number | null> = []
): Array<BuildableOffsetPreviewSegment | null> => {
  if (!Array.isArray(legalBorder) || legalBorder.length < 3 || !Array.isArray(segmentOffsets)) {
    return [];
  }

  const signedArea = getSignedArea(legalBorder);
  if (Math.abs(signedArea) <= MIN_LINE_LENGTH) {
    return [];
  }

  const isCounterClockwise = signedArea > 0;
  const shiftedLines: Array<{ start: Coordinate; end: Coordinate } | null> = legalBorder.map((start, index) => {
    const end = legalBorder[(index + 1) % legalBorder.length];
    const distance = segmentOffsets[index];

    if (distance == null || !Number.isFinite(Number(distance)) || Number(distance) < 0) {
      return null;
    }

    const inwardNormal = getInwardNormal(start, end, isCounterClockwise);
    if (!inwardNormal) {
      return null;
    }

    return {
      start: offsetPoint(start, inwardNormal, Number(distance)),
      end: offsetPoint(end, inwardNormal, Number(distance)),
    };
  });

  return shiftedLines.map((line, index) => {
    if (!line) {
      return null;
    }

    const previousLine = shiftedLines[(index - 1 + shiftedLines.length) % shiftedLines.length];
    const nextLine = shiftedLines[(index + 1) % shiftedLines.length];
    const previousIntersection = previousLine
      ? intersectInfiniteLines(previousLine.start, previousLine.end, line.start, line.end)
      : null;
    const nextIntersection = nextLine
      ? intersectInfiniteLines(line.start, line.end, nextLine.start, nextLine.end)
      : null;

    return {
      start:
        previousIntersection && Number.isFinite(previousIntersection.x) && Number.isFinite(previousIntersection.y)
          ? previousIntersection
          : line.start,
      end:
        nextIntersection && Number.isFinite(nextIntersection.x) && Number.isFinite(nextIntersection.y)
          ? nextIntersection
          : line.end,
    };
  });
};

export {
  createBuildableSegmentOffsets,
  deriveBuildableBorderFromLegalBorder,
  deriveBuildableOffsetPreviewSegments,
  getBuildableOffsetSourceBorder,
};
